import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { fingerprintScheduleEvent } from './fingerprintScheduleEvent'

const toTime = (value: string | Date): number =>
  value instanceof Date ? value.getTime() : new Date(value).getTime()

export const toScheduleEventId = (id: number | string | undefined): number => Number(id)

export const fingerprintSchedulePosition = (event: {
  id: number
  room_id?: string
  start_time: string | Date
  end_time: string | Date
}): string =>
  [event.id, event.room_id ?? '', toTime(event.start_time), toTime(event.end_time)].join('|')

export const fingerprintSchedulePositions = (
  events: Array<{
    id: number
    room_id?: string
    start_time: string | Date
    end_time: string | Date
  }>,
): string => events.map(fingerprintSchedulePosition).join(';')

export const isSameSchedulePosition = (a: IPatientSchedule, b: IPatientSchedule): boolean =>
  a.room_id === b.room_id &&
  toTime(a.start_time) === toTime(b.start_time) &&
  toTime(a.end_time) === toTime(b.end_time)

export type ScheduleEventDiff = {
  added: IPatientSchedule[]
  removed: IPatientSchedule[]
  contentChanged: IPatientSchedule[]
  positionChanged: IPatientSchedule[]
}

export const diffScheduleEvents = (
  prev: IPatientSchedule[],
  next: IPatientSchedule[],
): ScheduleEventDiff => {
  const prevById = new Map(prev.map((event) => [toScheduleEventId(event.id), event]))
  const nextIds = new Set(next.map((event) => toScheduleEventId(event.id)))

  const added: IPatientSchedule[] = []
  const contentChanged: IPatientSchedule[] = []
  const positionChanged: IPatientSchedule[] = []

  for (const event of next) {
    const previous = prevById.get(toScheduleEventId(event.id))
    if (!previous) {
      added.push(event)
    } else if (fingerprintScheduleEvent(previous) !== fingerprintScheduleEvent(event)) {
      ;(isSameSchedulePosition(previous, event) ? contentChanged : positionChanged).push(event)
    }
  }

  return {
    added,
    contentChanged,
    positionChanged,
    removed: prev.filter((event) => !nextIds.has(toScheduleEventId(event.id))),
  }
}

export const countScheduleEventChanges = (diff: ScheduleEventDiff): number =>
  diff.added.length + diff.removed.length + diff.contentChanged.length + diff.positionChanged.length

export const needsScheduleCollectionRefresh = (diff: ScheduleEventDiff): boolean =>
  diff.added.length > 0 || diff.removed.length > 0

type IdentifiableScheduleEvent = { id?: number | string }

export const patchLiveScheduleEvents = <T extends { id: number }>(
  dataSource: IdentifiableScheduleEvent[],
  updates: T[],
): { missingIds: number[] } => {
  const liveById = new Map(dataSource.map((event) => [toScheduleEventId(event.id), event]))
  const missingIds: number[] = []

  for (const update of updates) {
    const id = toScheduleEventId(update.id)
    const live = liveById.get(id)
    if (!live) {
      missingIds.push(id)
      continue
    }
    Object.assign(live, update)
  }

  return { missingIds }
}

type ScheduleEngine = {
  eventsData?: IdentifiableScheduleEvent[]
}

export const getScheduleEngineEvents = (
  schedule: ScheduleEngine | null,
): IdentifiableScheduleEvent[] | undefined => schedule?.eventsData

export const patchScheduleEngineEvents = <T extends { id: number }>(
  schedule: ScheduleEngine | null,
  updates: T[],
): { missingIds: number[] } => {
  const eventsData = getScheduleEngineEvents(schedule)
  if (!eventsData) {
    return { missingIds: updates.map((event) => toScheduleEventId(event.id)) }
  }
  return patchLiveScheduleEvents(eventsData, updates)
}
